import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { useQuery } from '@redux-requests/react';
import { isArray } from 'lodash-es';

import Loader from 'shared/components/Loader';

import { LoadingWrapperProps } from './types';

const StyledLoadingWrapperContainer = styled.div`
  display: flex;

  /* All its direct children will have these rules. */
  & > * {
    flex: 1;
  }
`;

const StyledLoaderContainer = styled.div<{isLoading?: boolean, dimOpacityWhenLoading?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;


  ${({isLoading, dimOpacityWhenLoading}) => dimOpacityWhenLoading && css`
    transition: opacity linear 0.25s;
    opacity: ${isLoading ? '0.7' : '1'};
    border: 1px solid #0000000f;
    border-radius: 10px;  `}
`;

/**
 * A wrapper for other components that renders a loading spinner in two ways
 * 
 * 
 * REDUX REQUEST MODE
 *  - Provide a single or an array of Redux Request Actions for the component to listen to.
 *  - Using Redux-Requests library, the component will know to show the loading spinner when the specified request actions are currently in progress
 * 
 *    Usage <LoadingWrapper actionToQuery={FETCH_SAFELIST_BY_GUID}> ... </LoadingWrapper>
 * 
 * 
 * MANUAL MODE
 * - Set showLoader with an outside prop, managed by an outside element / logic / anything
 * 
 *   Usage <LoadingWrapper showLoader={SOME_CONDITION}> ... </LoadingWrapper>
 * 
 * @param dimOpacityWhenLoading: Boolean - Dim the loading wrapper container during loading, provides a clearer 'this thing is not available and loading is in progress' visual to the user 
 * 
 * NOTE: Component accepts either actionToQuery   OR   showLoader, cannot be both!
 */
const LoadingWrapper = ({
  actionToQuery,
  children,
  className,
  loaderClassName,
  showLoader,  
  dimOpacityWhenLoading,
}: LoadingWrapperProps) => {
  const [isLoading, setIsLoading] = useState(true);
  let loadingFlag: any = false;
  // Default is assumed to be redux mode, so showLoader is put in the if. The type is meant to ENSURE the component has either showLoader or actionToQuery, cannot have both or neither.
  if (showLoader && !actionToQuery) {
    loadingFlag = showLoader;
  } else if (actionToQuery) {
    const actionTypes = isArray(actionToQuery) ? actionToQuery : [actionToQuery];
    loadingFlag = actionTypes.reduce((accumulatorFlag, actionType) => {
      const { loading } = useQuery({ type: actionType as string }); // actionType casted to string to avoid TS error about possible undefined (an assumption it inherits from using the .reduce function)
  
      return accumulatorFlag || loading;
    }, false);
  }

  useEffect(() => {
    setIsLoading(loadingFlag);
  }, [loadingFlag]);

  return (
    <StyledLoadingWrapperContainer className={className}>
      {isLoading
        ? <StyledLoaderContainer isLoading={isLoading} dimOpacityWhenLoading={dimOpacityWhenLoading}>
            <Loader
              className={loaderClassName}
              fadeIn='quarter'
              name='line-scale'
            />
          </StyledLoaderContainer>
        : children
      }
    </StyledLoadingWrapperContainer>
  );
};

export default LoadingWrapper;
